import {useFeatureIsOn} from "@growthbook/growthbook-react";
import {useMemo} from "react";

import {useQuery, type GqlQueryOptions} from "@khan/data-gql";
import {useIsCountryBlocked} from "@khan/khanmigo-access";
import {PermissionsInfo, RequestInfo} from "@khan/shared-core";

import type {
    getTeacherAccessPermissionsData as GqlTeacherAccessPermissionsDataResponse,
    getTeacherAccessPermissionsDataVariables as GqlTeacherAccessPermissionsDataVariables,
    getTeacherAccessPermissionsData_user as UserData,
} from "./__graphql-types__/getTeacherAccessPermissionsData.ts";
import {TEACHER_ACCESS_PERMISSIONS_DATA} from "./getTeacherAccessPermissionsData.ts";

type Maybe<T> = T | null | undefined;

export type TeacherAccessPermissionsResult =
    | {
          status: "loading";
          data: null;
      }
    | {
          status: "error";
          data: null;
      }
    | {
          status: "success";
          data: {
              isEligibleForKhanmigoTeacherTools: boolean;
              canAccessKhanmigoTeacherTools: boolean;
              canAssignAssignments: boolean;
              canPreviewLoggedOutTools?: boolean;
              canAccessAssessments?: boolean;
          };
      };

// NOTE: we only validate the kaLocale on the frontend.
// The backend activation is here: services/ai-guide/teacher_tools/activate.go
const KHANMIGO_SUPPORTED_LOCALES: Record<string, boolean> = {
    en: true, // English
    es: true, // Spanish
    pt: true, // Portuguese
    hi: true, // Hindi
    vi: true, // Vietnamese
};

/**
 * This hook determines whether or not a teacher may be eligible for various
 * teacher tools, including Khanmigo and assignment creation, and returns a
 * memoized result.
 */
const useTeacherAccessPermissionsQuery = (
    options: GqlQueryOptions<
        GqlTeacherAccessPermissionsDataResponse,
        GqlTeacherAccessPermissionsDataVariables
    > = {},
): TeacherAccessPermissionsResult => {
    const [queryResult] = useQuery(TEACHER_ACCESS_PERMISSIONS_DATA, options);
    const ttInPKFlag = useFeatureIsOn("teacher-tools-in-pakistan");

    const isAssessmentsManagementEnabled = useFeatureIsOn(
        "assessments-management",
    );
    const isPKandPassesFlag = RequestInfo.remoteCountry === "PK" && ttInPKFlag;
    const isRemoteCountryBlocked = useIsCountryBlocked(
        RequestInfo.remoteCountry,
    );

    const isEligibleForKhanmigoTeacherTools =
        queryResult.status === "success" &&
        getIsEligibleForKhanmigoTeacherTools(
            queryResult.data.user,
            isPKandPassesFlag,
            isRemoteCountryBlocked,
        );
    const canAccessKhanmigoTeacherTools =
        queryResult.status === "success" &&
        getCanAccessKhanmigoTeacherTools(queryResult.data.user);
    const canAssignAssignments =
        queryResult.status === "success" &&
        getCanAssignAssignments(queryResult.data.user);
    const canPreviewLoggedOutTools =
        queryResult.status === "success" &&
        getCanPreviewLoggedOutTools(queryResult.data.user, true);

    const canAccessAssessments =
        queryResult.status === "success" &&
        getCanAccessAssessments(
            queryResult.data.user?.userDistrictInfos,
            isAssessmentsManagementEnabled,
        );

    const memoizedResult = useMemo<TeacherAccessPermissionsResult>(() => {
        // If query is loading, return loading.
        if (queryResult.status === "loading") {
            return {status: "loading", data: null};
        }

        // If query fails, return error.
        if (queryResult.status !== "success") {
            return {status: "error", data: null};
        }

        return {
            status: "success",
            data: {
                isEligibleForKhanmigoTeacherTools,
                canAccessKhanmigoTeacherTools,
                canAssignAssignments,
                canPreviewLoggedOutTools,
                canAccessAssessments,
            },
        };
    }, [
        queryResult.status,
        isEligibleForKhanmigoTeacherTools,
        canAccessKhanmigoTeacherTools,
        canAssignAssignments,
        canPreviewLoggedOutTools,
        canAccessAssessments,
    ]);
    return memoizedResult;
};

/**
 * We may not have enough information to determine whether or not a teacher
 * is actually eligible (e.g. they may not have provided their age) but we
 * can rule out some teachers who are definitely ineligible.
 * Returns true if maybe eligible, false if definitely ineligible.
 * This is used to turn on TX-owned in-product marketing for Free Khanmigo.
 * */
function getIsEligibleForKhanmigoTeacherTools(
    user: Maybe<UserData>,
    isPKandPassesFlag: boolean,
    isRemoteCountryBlocked: boolean,
): boolean {
    if (!user) {
        return false;
    }

    const supportedLocale = Boolean(
        KHANMIGO_SUPPORTED_LOCALES[RequestInfo.kaLocale],
    );

    // We're about to allow teacher tools in Pakistan. For now we want this
    // behind a flag so we can launch in a controlled way.
    let supportedCountry = false;
    if (isPKandPassesFlag) {
        supportedCountry = true;
    } else {
        // Users must be in one of the WHAM International supported countries.
        supportedCountry = !isRemoteCountryBlocked;
    }

    const isUnderAge = user.age && user.age < 18;
    const alreadyHasAccess = user.hasAccessToAIGuideTeacher;
    // Check if a user is a grassroots or district teacher.
    const isTeacher = !!(user.isTeacher || user.isFormalTeacher);
    const isDistrictsStudent = !!(user.isK4dStudent || user.isKmapStudent);

    const isNewKADTeacher = isJustJoinedKADTeacher(user);

    return (
        supportedLocale &&
        supportedCountry &&
        !isUnderAge &&
        !alreadyHasAccess &&
        isTeacher &&
        !isDistrictsStudent &&
        !isNewKADTeacher
    );
}

/**
 * Returns true if the user is a teacher and has access to Khanmigo Teacher Tools.
 * This is used to determine whether to show the tools in the UI.
 */
function getCanAccessKhanmigoTeacherTools(user: Maybe<UserData>): boolean {
    if (!user) {
        return false;
    }
    return !!(user.hasAccessToAIGuideTeacher && user.isAIGuideEnabled);
}

/**
 * Returns true if the user is a teacher and has classes or if the user is a
 * parent and has non-KAD children in ChildAssignmentsClassrooms.
 * They MUST have a class to offer assignments.
 * This is used to determine whether to show the "Assign" CTA's.
 */
function getCanAssignAssignments(user: Maybe<UserData>): boolean {
    if (!user) {
        return false;
    }
    const canAssignAsTeacher = !!(
        (user.isTeacher || user.isFormalTeacher || user.isK4dTeacher) &&
        user.hasClasses
    );
    /** Prevent checking for Parent assignability if daper2flag is off.
     * This will become just an early return after the daper2flag is always true.
     */
    if (canAssignAsTeacher) {
        return canAssignAsTeacher; // early return to avoid unnecessary checks
    }
    const canAssignAsParent = !!(
        user.hasChildren &&
        user.coachedChildAssignmentsClassrooms.length > 0 &&
        user.coachedChildAssignmentsClassrooms.some((classroom) => {
            /** Check that the classroom has no students that are a part of a district.
             * `every` is performant because ChildAssignmentsClassrooms are limited to a single student.
             * `every` is used over `some` to safeguard other parts of the system where we assume the
             * parent wants to assign an assignment to all students in a CAC.
             */
            return classroom.students?.every((student) => {
                return !student.includesDistrictOwnedData;
            });
        })
    );
    return canAssignAsParent;
}

function getCanPreviewLoggedOutTools(
    user: Maybe<UserData>,
    loggedOutToolsFeatureEnabled: boolean,
): boolean {
    if (!user) {
        return false;
    }
    const isLoggedOut = !PermissionsInfo.isLoggedIn;
    // Phantom users may have the "teacher" role
    return !!(loggedOutToolsFeatureEnabled && user.isTeacher && isLoggedOut);
}

/**
 * Returns true if the user has access to any assessments.
 */
function getCanAccessAssessments(
    userDistrictInfos: UserData["userDistrictInfos"],
    isAssessmentsManagementEnabled: boolean,
): boolean {
    return (
        isAssessmentsManagementEnabled &&
        userDistrictInfos != null &&
        userDistrictInfos.some(
            (userDistrictInfo) =>
                userDistrictInfo?.district?.hasAssessments ||
                userDistrictInfo?.district?.hasAssessmentsPilots,
        )
    );
}

/**
 * Is this a brand new KAD Teacher? We'd like to hide teacher tools promos
 * for them since they're going to be shown a series of onboarding popups.
 * The first thing the user sees is the KAD Teacher onboarding popup, and
 * they immediately close it. So anyone viewing this page 5 minutes after
 * they joined can safely be shown the teacher tools promos.
 */
function isJustJoinedKADTeacher(user: UserData): boolean {
    const minutesBeforeConsideredNotNew = 5;
    return !!(
        user.isK4dTeacher &&
        user.joined &&
        joinedHowManyMinutesAgo(new Date(user.joined)) <
            minutesBeforeConsideredNotNew
    );
}

function joinedHowManyMinutesAgo(joined: Date): number {
    const now = new Date();
    const diffInMs = now.getTime() - joined.getTime();

    // Dividing by 1000 to get seconds, then by 60 to get minutes.
    return diffInMs / 1000 / 60;
}

export default useTeacherAccessPermissionsQuery;
